/**
 * Smart-Money / Midcap-Alpha Portfolio Tab — Session 148 v5 (Feb 2026).
 *
 * Engine-agnostic portfolio tab. Renders EITHER the delivery-divergence
 * engine (default) OR the Midcap-Alpha v5 turnover-breakout engine
 * depending on the `engine` prop.
 *
 *   engine = "delivery"      →  /api/smart-money/*
 *   engine = "midcap-alpha"  →  /api/midcap-alpha/*
 *
 * Backend `/summary` returns per-engine stats including total_pnl_inr,
 * winner_pnl_inr, loser_pnl_inr, and `breakdown_by_close_reason` for
 * the closed positions — rendered in the KPI strip and the "Losers
 * Breakdown" panel below.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, Cell,
} from 'recharts';
import {
  ArrowUp, ArrowDown, Target, ShieldAlert, Clock, RefreshCw, TrendingUp, TrendingDown, Zap, Flame, IndianRupee, TrendingUpIcon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ENGINE_CONFIG = {
  'delivery': {
    label: 'Smart-Money (Delivery)',
    routePrefix: '/api/smart-money',
    adminPrefix: '/api/admin/smart-money',
    icon: Zap,
    accent: 'amber',
    tagline: 'Delivery-divergence swing tracker',
  },
  'midcap-alpha': {
    label: 'Midcap-Alpha (v5 Turnover Breakout)',
    routePrefix: '/api/midcap-alpha',
    adminPrefix: '/api/admin/midcap-alpha',
    icon: Flame,
    accent: 'fuchsia',
    tagline: 'Turnover-breakout swing tracker (F1+F3+F4 gates · SMART_EXIT volume-exhaustion)',
  },
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmt = (v, digits = 2) => (v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(digits));
const fmtPct = (v, digits = 2) => (v == null || Number.isNaN(v) ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(digits)}%`);
const fmtInr = (v) => {
  if (v == null || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(1)}k`;
  return `${sign}₹${abs.toFixed(0)}`;
};
const toneForPct = (v) => {
  if (v == null) return 'text-zinc-400';
  if (v > 5) return 'text-emerald-400 font-semibold';
  if (v > 0) return 'text-emerald-300';
  if (v > -5) return 'text-rose-300';
  return 'text-rose-400 font-semibold';
};
const toneForInr = (v) => {
  if (v == null) return 'text-zinc-400';
  return v >= 0 ? 'text-emerald-400 font-semibold' : 'text-rose-400 font-semibold';
};
const TIER_TONE = {
  EXTREME: 'bg-amber-500/20 text-amber-200 border-amber-500/40',
  STRONG:  'bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-500/40',
  MODERATE:'bg-sky-500/10 text-sky-200 border-sky-500/40',
};
const CLOSE_REASON_TONE = {
  TARGET:      'bg-emerald-500/20 text-emerald-200 border-emerald-500/40',
  PROFIT_LOCK: 'bg-teal-500/20 text-teal-200 border-teal-500/40',
  SMART_EXIT:  'bg-cyan-500/20 text-cyan-200 border-cyan-500/40',
  STOP:        'bg-rose-500/20 text-rose-200 border-rose-500/40',
  TIME:        'bg-zinc-500/20 text-zinc-200 border-zinc-500/40',
};

// ─── KPI strip ────────────────────────────────────────────────
const KPI = ({ label, value, tone, sub, testId }) => (
  <div
    className="rounded border border-zinc-800 bg-zinc-950/60 p-3 min-w-[140px]"
    data-testid={testId}
  >
    <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`text-xl font-bold ${tone || 'text-zinc-100'}`}>{value}</div>
    {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

// ─── Hero ROI panel (marketing-friendly portfolio return) ────
// `avg return per stock` is misleading. What matters for a portfolio is
// PnL vs peak capital deployed (a proxy for the actual portfolio size
// needed to run this strategy), annualised over the backtest span.
const HeroROI = ({ portfolioRoi, engineColor }) => {
  if (!portfolioRoi || portfolioRoi.total_pnl_inr == null) return null;
  const {
    total_pnl_inr, peak_concurrent_capital_inr, roi_on_peak_capital_pct,
    annualized_return_pct, backtest_span_days, backtest_start_date, backtest_end_date,
    position_count, roi_on_deployed_pct,
  } = portfolioRoi;
  const posClr = total_pnl_inr >= 0 ? 'text-emerald-300' : 'text-rose-300';
  const roiClr = roi_on_peak_capital_pct >= 0 ? 'text-emerald-400' : 'text-rose-400';
  const annClr = (annualized_return_pct ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400';
  const grad = engineColor === 'fuchsia'
    ? 'from-fuchsia-500/15 via-fuchsia-500/5 to-zinc-950 border-fuchsia-500/30'
    : 'from-amber-500/15 via-amber-500/5 to-zinc-950 border-amber-500/30';
  return (
    <div
      className={`rounded-lg border bg-gradient-to-br ${grad} p-5 shadow-lg`}
      data-testid="sm-hero-roi"
    >
      <div className="flex items-center gap-2 mb-3">
        <IndianRupee className="w-4 h-4 text-zinc-300" />
        <h2 className="font-semibold text-zinc-100 text-sm uppercase tracking-widest">
          Portfolio Performance
        </h2>
        <span className="text-[11px] text-zinc-500">
          {position_count} trades · {backtest_start_date} → {backtest_end_date} · {backtest_span_days}d span
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Portfolio ROI</div>
          <div className={`text-3xl md:text-4xl font-bold ${roiClr}`} data-testid="sm-hero-roi-pct">
            {roi_on_peak_capital_pct > 0 ? '+' : ''}{roi_on_peak_capital_pct}%
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">on ₹{(peak_concurrent_capital_inr / 1e5).toFixed(1)}L peak deployed</div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Annualised</div>
          <div className={`text-3xl md:text-4xl font-bold ${annClr}`} data-testid="sm-hero-annualized">
            {annualized_return_pct != null ? `${annualized_return_pct > 0 ? '+' : ''}${annualized_return_pct}%` : '—'}
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">projected p.a.</div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Total PnL</div>
          <div className={`text-3xl md:text-4xl font-bold ${posClr}`} data-testid="sm-hero-total-pnl">
            {total_pnl_inr >= 0 ? '+' : ''}₹{(total_pnl_inr / 1e5).toFixed(2)}L
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">realised + unrealised</div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Peak Capital</div>
          <div className="text-3xl md:text-4xl font-bold text-zinc-100">
            ₹{(peak_concurrent_capital_inr / 1e5).toFixed(1)}L
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">max concurrent deployed</div>
        </div>
      </div>
      <div className="text-[10px] text-zinc-500 mt-3 leading-relaxed italic">
        Portfolio ROI = Total PnL ÷ Peak concurrent capital.
        Per-trade avg return ({roi_on_deployed_pct}%) is diluted by capital velocity across {position_count} sequential trades.
        Annualised uses the {backtest_span_days}-day backtest span (compound-growth projection).
      </div>
    </div>
  );
};

const KpiStrip = ({ summary, mode }) => {
  if (!summary) return null;
  const stats = summary[mode] || {};
  return (
    <div className="flex flex-wrap gap-3" data-testid="sm-kpi-strip">
      <KPI
        testId="sm-kpi-count"
        label={`${mode} positions`}
        value={stats.count ?? 0}
        sub={mode === 'history' && stats.winners != null ? `${stats.winners}W · ${stats.losers}L` : null}
      />
      <KPI
        testId="sm-kpi-hit-rate"
        label="Hit rate"
        value={stats.hit_rate_pct != null ? `${stats.hit_rate_pct}%` : '—'}
        tone={stats.hit_rate_pct > 55 ? 'text-emerald-400' : stats.hit_rate_pct >= 45 ? 'text-amber-300' : 'text-rose-400'}
      />
      <KPI
        testId="sm-kpi-avg-return"
        label="Avg return"
        value={fmtPct(stats.avg_return_pct)}
        tone={toneForPct(stats.avg_return_pct)}
      />
      <KPI
        testId="sm-kpi-median-return"
        label="Median return"
        value={fmtPct(stats.median_return_pct)}
        tone={toneForPct(stats.median_return_pct)}
      />
      {mode === 'history' && (
        <>
          <KPI
            testId="sm-kpi-total-pnl"
            label="Total PnL"
            value={fmtInr(stats.total_pnl_inr)}
            tone={toneForInr(stats.total_pnl_inr)}
            sub={`Win ${fmtInr(stats.winner_pnl_inr)} · Loss ${fmtInr(stats.loser_pnl_inr)}`}
          />
        </>
      )}
      <KPI
        testId="sm-kpi-best"
        label="Best"
        value={fmtPct(stats.best_return_pct)}
        tone="text-emerald-400"
      />
      <KPI
        testId="sm-kpi-worst"
        label="Worst"
        value={fmtPct(stats.worst_return_pct)}
        tone="text-rose-400"
      />
      {mode === 'history' && (
        <>
          <KPI
            testId="sm-kpi-target-hits"
            label="Target hits"
            value={stats.target_hits ?? 0}
            tone="text-emerald-300"
          />
          <KPI
            testId="sm-kpi-profit-lock-hits"
            label="Profit-lock hits"
            value={stats.profit_lock_hits ?? 0}
            tone="text-teal-300"
          />
          <KPI
            testId="sm-kpi-smart-exit-hits"
            label="Smart-exit hits"
            value={stats.smart_exit_hits ?? 0}
            tone="text-cyan-300"
          />
          <KPI
            testId="sm-kpi-stop-hits"
            label="Stop hits"
            value={stats.stop_hits ?? 0}
            tone="text-rose-300"
          />
          <KPI
            testId="sm-kpi-time-hits"
            label="Timeouts"
            value={stats.time_hits ?? 0}
            tone="text-zinc-300"
          />
        </>
      )}
    </div>
  );
};

// ─── Breakdown panel ──────────────────────────────────────────
const BreakdownPanel = ({ breakdown }) => {
  const rows = useMemo(() => {
    if (!breakdown || typeof breakdown !== 'object') return [];
    return Object.entries(breakdown).map(([reason, b]) => ({ reason, ...b }))
      .sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [breakdown]);
  if (!rows.length) return null;
  return (
    <div className="rounded border border-zinc-800 bg-zinc-950/40 p-4" data-testid="sm-breakdown-panel">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUpIcon className="w-4 h-4 text-zinc-300" />
        <h2 className="font-semibold text-zinc-100 text-sm">Closed positions breakdown</h2>
        <span className="text-[11px] text-zinc-500">grouped by exit reason · winners vs losers</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-zinc-500 uppercase text-[9px] tracking-wider border-b border-zinc-800">
              <th className="px-2 py-1.5 text-left">Exit Reason</th>
              <th className="px-2 py-1.5 text-right">Count</th>
              <th className="px-2 py-1.5 text-right">Wins</th>
              <th className="px-2 py-1.5 text-right">Losses</th>
              <th className="px-2 py-1.5 text-right">Avg Return</th>
              <th className="px-2 py-1.5 text-right">Avg Peak Gain</th>
              <th className="px-2 py-1.5 text-right">Avg Days Held</th>
              <th className="px-2 py-1.5 text-right">Total PnL</th>
            </tr>
          </thead>
          <tbody data-testid="sm-breakdown-tbody">
            {rows.map((r) => (
              <tr key={r.reason} className="border-b border-zinc-900 hover:bg-zinc-900/30" data-testid={`sm-breakdown-row-${r.reason}`}>
                <td className="px-2 py-1.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${CLOSE_REASON_TONE[r.reason] || ''}`}>
                    {r.reason}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-right font-mono text-zinc-100 font-semibold">{r.count}</td>
                <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{r.wins}</td>
                <td className="px-2 py-1.5 text-right font-mono text-rose-300">{r.losses}</td>
                <td className={`px-2 py-1.5 text-right font-mono ${toneForPct(r.avg_return_pct)}`}>
                  {fmtPct(r.avg_return_pct)}
                </td>
                <td className="px-2 py-1.5 text-right font-mono text-zinc-400">
                  {r.avg_peak_gain_pct != null ? fmtPct(r.avg_peak_gain_pct) : '—'}
                </td>
                <td className="px-2 py-1.5 text-right font-mono text-zinc-400">{fmt(r.avg_days_held, 1)}</td>
                <td className={`px-2 py-1.5 text-right font-mono font-semibold ${toneForInr(r.total_pnl_inr)}`}>
                  {fmtInr(r.total_pnl_inr)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-zinc-500 italic mt-2 leading-relaxed">
        SMART_EXIT = volume-exhaustion algo (fires when spike momentum dies out).
        PROFIT_LOCK = giveback triggered after ≥10% peak.
        STOP = hard -10% stop-loss. TIME = 21-day max-hold timeout.
      </p>
    </div>
  );
};

// ─── Returns bar chart ────────────────────────────────────────
const ReturnBarChart = ({ positions }) => {
  const data = useMemo(
    () => positions
      .map((p) => ({
        symbol: p.symbol,
        return_pct: p.status === 'open' ? p.return_pct : p.closed_return_pct,
      }))
      .filter((x) => x.return_pct != null)
      .sort((a, b) => b.return_pct - a.return_pct)
      .slice(0, 40),
    [positions],
  );
  if (!data.length) return null;
  return (
    <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3" data-testid="sm-return-chart">
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
        Returns by position (top 40)
      </div>
      <div style={{ width: '100%', height: 220, minHeight: 220 }}>
        <ResponsiveContainer width="100%" height="100%" minHeight={220}>
          <BarChart data={data} margin={{ top: 6, right: 6, left: -12, bottom: 20 }}>
            <XAxis dataKey="symbol" tick={{ fill: '#71717a', fontSize: 9 }} angle={-45} textAnchor="end" interval={0} />
            <YAxis tick={{ fill: '#71717a', fontSize: 10 }} unit="%" />
            <RTooltip
              contentStyle={{ background: '#0a0a0a', border: '1px solid #3f3f46', fontSize: 11 }}
              formatter={(v) => [`${v.toFixed(2)}%`, 'Return']}
              labelStyle={{ color: '#e4e4e7' }}
            />
            <Bar dataKey="return_pct">
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.return_pct >= 0 ? '#10b981' : '#f43f5e'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ─── Watchlist panel ──────────────────────────────────────────
const WatchlistPanel = ({ data, loading, onRefresh, accentClass }) => {
  const rows = data?.rows || [];
  return (
    <div className={`rounded border ${accentClass} p-4 space-y-2`} data-testid="sm-watchlist-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-300" />
          <h2 className="font-semibold text-zinc-100">Live Conviction Watchlist</h2>
          <span className="text-[11px] text-zinc-500">
            {data?.date ? `as of ${data.date}` : 'no data'} · {rows.length} names ≥ 6.0
          </span>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="text-[11px] px-3 py-1.5 border border-zinc-700 hover:border-amber-400/60 rounded flex items-center gap-1.5 disabled:opacity-50"
          data-testid="sm-watchlist-refresh-btn"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      <p className="text-[11px] text-zinc-500 italic leading-relaxed">
        Conviction Score = Persistence (0-4) + Best tier (0-3) + Escalation (0-1.5) + Price alignment (0-1.5).
        Symbols ≥ 8.0 will auto-open on the next trading day.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-zinc-500 uppercase text-[9px] tracking-wider border-b border-zinc-800">
              <th className="px-2 py-1.5 text-left">Symbol</th>
              <th className="px-2 py-1.5 text-center text-[9px]">Trigger Date</th>
              <th className="px-2 py-1.5 text-right">Trigger Price</th>
              <th className="px-2 py-1.5 text-right">Score</th>
              <th className="px-2 py-1.5 text-center">Best Tier</th>
              <th className="px-2 py-1.5 text-right">Days Fired</th>
              <th className="px-2 py-1.5 text-right">Signal</th>
              <th className="px-2 py-1.5 text-right">Deliv %</th>
              <th className="px-2 py-1.5 text-right">EOD Price</th>
              <th className="px-2 py-1.5 text-center">Ready?</th>
              <th className="px-2 py-1.5 text-left text-[8px]">Breakdown (P·T·E·A)</th>
            </tr>
          </thead>
          <tbody data-testid="sm-watchlist-tbody">
            {rows.length === 0 && (
              <tr><td colSpan={11} className="py-6 text-center text-zinc-500 italic">No qualifying symbols today.</td></tr>
            )}
            {rows.map((r) => {
              // "Signal" column adapts per-engine: Z-score for delivery, Spike× for midcap-alpha
              const signal = r.latest_z_score != null
                ? fmt(r.latest_z_score)
                : (r.latest_spike_ratio != null ? `${fmt(r.latest_spike_ratio, 1)}×` : '—');
              // Trigger date/price are pinned once set — represent when the
              // symbol first entered the current watchlist streak. Fall back
              // to '—' for pre-2026-02-09 rows that predate this feature.
              const triggerDate = r.trigger_date || '—';
              const triggerPrice = r.trigger_price != null ? `₹${fmt(r.trigger_price)}` : '—';
              // Price move since trigger — helps eyeball recommendation performance
              const moveSinceTrigger =
                r.trigger_price != null && r.latest_close != null
                  ? ((r.latest_close - r.trigger_price) / r.trigger_price) * 100
                  : null;
              return (
                <tr key={r.symbol} className="border-b border-zinc-900 hover:bg-zinc-900/30" data-testid={`sm-watchlist-row-${r.symbol}`}>
                  <td className="px-2 py-1.5 font-mono font-semibold text-zinc-100" data-testid={`sm-watchlist-symbol-${r.symbol}`}>{r.symbol}</td>
                  <td
                    className="px-2 py-1.5 text-center font-mono text-zinc-400 text-[10px]"
                    title={r.trigger_date ? `First entered watchlist on ${r.trigger_date}. Pinned — does not update.` : 'Trigger date pending — will populate on next daily recompute.'}
                    data-testid={`sm-watchlist-trigger-date-${r.symbol}`}
                  >{triggerDate}</td>
                  <td
                    className="px-2 py-1.5 text-right font-mono text-zinc-300"
                    title={r.trigger_price != null ? `Close on ${r.trigger_date} when symbol entered watchlist.` : ''}
                    data-testid={`sm-watchlist-trigger-price-${r.symbol}`}
                  >{triggerPrice}</td>
                  <td className={`px-2 py-1.5 text-right font-mono font-bold ${r.score >= 8 ? 'text-emerald-300' : 'text-amber-300'}`}>{fmt(r.score)}</td>
                  <td className="px-2 py-1.5 text-center">
                    {r.best_tier && (
                      <span className={`text-[9px] px-1.5 py-0.5 rounded border ${TIER_TONE[r.best_tier] || ''}`}>
                        {r.best_tier}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-zinc-300">{r.days_fired}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-zinc-300">{signal}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-zinc-300">{fmt(r.latest_deliv_pct, 1)}%</td>
                  <td
                    className="px-2 py-1.5 text-right font-mono text-zinc-100"
                    title={
                      moveSinceTrigger != null
                        ? `EOD close on ${r.latest_close_date || r.date}. ${moveSinceTrigger >= 0 ? '+' : ''}${fmt(moveSinceTrigger, 2)}% since trigger.`
                        : `EOD close on ${r.latest_close_date || r.date}`
                    }
                    data-testid={`sm-watchlist-eod-price-${r.symbol}`}
                  >₹{fmt(r.latest_close)}</td>
                  <td className="px-2 py-1.5 text-center">
                    {r.is_portfolio_ready ? (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-200 border border-emerald-500/40">READY</span>
                    ) : (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800/50 text-zinc-400 border border-zinc-700">watch</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-[10px] text-zinc-500 font-mono">
                    {fmt(r.components?.persistence_pts, 1)} · {fmt(r.components?.tier_pts, 1)} · {fmt(r.components?.escalation_pts, 1)} · {fmt(r.components?.price_align_pts, 1)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Today's Actions panel — trader-focused 3-list summary ────
const TodayActionsPanel = ({ watchlist, openPositions, historyPositions, engineColor, cfg }) => {
  // "Buy tomorrow" — READY watchlist rows (score ≥ 8.0)
  const readyBuys = (watchlist?.rows || [])
    .filter((r) => r.is_portfolio_ready)
    .slice(0, 15);
  // Currently holding — open positions
  const holds = (openPositions || []).slice(0, 20);
  // Recently closed — most recent 5 by close_date
  const recentCloses = (historyPositions || [])
    .slice()
    .sort((a, b) => (b.close_date || '').localeCompare(a.close_date || ''))
    .slice(0, 5);
  const asOf = watchlist?.date || '—';
  const accent = engineColor === 'fuchsia' ? 'border-fuchsia-500/40 bg-fuchsia-500/5' : 'border-amber-500/40 bg-amber-500/5';
  const target = cfg?.target_pct ?? 25;
  const stop = cfg?.stop_pct ?? 10;

  return (
    <div className={`rounded border ${accent} p-4 space-y-4`} data-testid="today-actions-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-zinc-100">📋 Today&apos;s Actions</h2>
          <p className="text-[11px] text-zinc-500">
            Book decisions the engine has made. Watchlist as of {asOf}. Auto-opens next trading day.
          </p>
        </div>
      </div>

      {/* Three-column grid */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* 1) TO BUY TOMORROW */}
        <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="today-buys">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-emerald-200 uppercase tracking-wider">🟢 Buy Next Trading Day</span>
            <span className="text-[10px] text-zinc-400">{readyBuys.length}</span>
          </div>
          {readyBuys.length === 0 ? (
            <p className="text-[11px] text-zinc-500 italic">No READY names right now.</p>
          ) : (
            <ul className="space-y-1 max-h-72 overflow-auto text-xs">
              {readyBuys.map((r) => (
                <li key={r.symbol} className="flex items-center justify-between py-1 border-b border-zinc-800/50 last:border-0" data-testid={`today-buy-${r.symbol}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-zinc-100">{r.symbol}</span>
                    <span className="text-[9px] px-1 rounded bg-zinc-800/50 text-zinc-400">{r.best_tier}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-emerald-200 font-bold">{fmt(r.score)}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">₹{fmt(r.latest_close)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 2) HOLDING NOW */}
        <div className="rounded border border-sky-500/30 bg-sky-500/5 p-3" data-testid="today-holds">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-sky-200 uppercase tracking-wider">🟡 Currently Holding</span>
            <span className="text-[10px] text-zinc-400">{holds.length}</span>
          </div>
          {holds.length === 0 ? (
            <p className="text-[11px] text-zinc-500 italic">No open positions.</p>
          ) : (
            <ul className="space-y-1 max-h-72 overflow-auto text-xs">
              {holds.map((p) => {
                const ret = p.return_pct ?? 0;
                const retColor = ret >= 0 ? 'text-emerald-200' : 'text-rose-200';
                return (
                  <li key={p._id || p.symbol} className="flex items-center justify-between py-1 border-b border-zinc-800/50 last:border-0" data-testid={`today-hold-${p.symbol}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-zinc-100">{p.symbol}</span>
                      <span className="text-[9px] text-zinc-500">D{p.trading_days_elapsed ?? 0}</span>
                    </div>
                    <div className="text-right">
                      <div className={`font-mono font-bold ${retColor}`}>{ret >= 0 ? '+' : ''}{fmt(ret, 2)}%</div>
                      <div className="text-[10px] text-zinc-500 font-mono">now ₹{fmt(p.latest_price ?? p.entry_price)}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="text-[10px] text-zinc-500 mt-2">
            Auto-exits at +{target}% target, −{stop}% stop, or on SMART_EXIT signal.
          </p>
        </div>

        {/* 3) RECENTLY CLOSED */}
        <div className="rounded border border-zinc-700 bg-zinc-800/20 p-3" data-testid="today-closed">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-zinc-300 uppercase tracking-wider">⚪ Recently Closed</span>
            <span className="text-[10px] text-zinc-400">Last 5</span>
          </div>
          {recentCloses.length === 0 ? (
            <p className="text-[11px] text-zinc-500 italic">No closed trades yet.</p>
          ) : (
            <ul className="space-y-1 text-xs">
              {recentCloses.map((p) => {
                const ret = p.return_pct ?? p.closed_return_pct ?? 0;
                const retColor = ret >= 0 ? 'text-emerald-200' : 'text-rose-200';
                return (
                  <li key={p._id || `${p.symbol}-${p.close_date}`} className="flex items-center justify-between py-1 border-b border-zinc-800/50 last:border-0" data-testid={`today-closed-${p.symbol}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-zinc-100">{p.symbol}</span>
                      <span className="text-[9px] text-zinc-500">{p.close_reason}</span>
                    </div>
                    <div className="text-right">
                      <div className={`font-mono font-bold ${retColor}`}>{ret >= 0 ? '+' : ''}{fmt(ret, 2)}%</div>
                      <div className="text-[10px] text-zinc-500 font-mono">{p.close_date}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Positions table ──────────────────────────────────────────
const HEADERS = [
  { id: 'symbol', label: 'Symbol', numeric: false },
  { id: 'entry_date', label: 'Entry', numeric: false },
  { id: 'trading_days_elapsed', label: 'Days', numeric: true },
  { id: 'conviction_score_at_entry', label: 'Conv', numeric: true },
  { id: 'entry_price', label: 'Entry ₹', numeric: true },
  { id: 'target_price', label: 'TP ₹', numeric: true },
  { id: 'stop_price', label: 'SL ₹', numeric: true },
  { id: 'latest_price', label: 'Now ₹', numeric: true },
  { id: 'return_pct', label: 'Return %', numeric: true },
  { id: 'return_vs_nifty500_pct', label: 'vs N500', numeric: true },
  { id: 'max_gain_pct', label: 'Max Gain', numeric: true },
  { id: 'max_loss_pct', label: 'Max Loss', numeric: true },
  { id: 'close_reason', label: 'Close', numeric: false },
];

const PositionsTable = ({ positions, sortKey, sortDir, onSort }) => (
  <div className="rounded border border-zinc-800 bg-zinc-950/40 overflow-x-auto" data-testid="sm-positions-table">
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-zinc-800 text-zinc-500 uppercase text-[9px] tracking-wider">
          {HEADERS.map((h) => (
            <th
              key={h.id}
              className={`px-2 py-1.5 ${h.numeric ? 'text-right' : 'text-left'} cursor-pointer hover:text-zinc-200 select-none whitespace-nowrap`}
              onClick={() => onSort(h.id)}
              data-testid={`sm-th-${h.id}`}
            >
              {h.label}
              {sortKey === h.id && (
                <span className="ml-0.5 inline-flex align-middle">
                  {sortDir === 'asc' ? <ArrowUp size={9} /> : <ArrowDown size={9} />}
                </span>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody data-testid="sm-positions-tbody">
        {positions.length === 0 && (
          <tr>
            <td colSpan={HEADERS.length} className="py-8 text-center text-zinc-500 italic">
              No positions yet. Run the backfill above to seed from cached signal history,
              or wait for the daily cron (18:30 IST) to open new positions from live watchlist fires.
            </td>
          </tr>
        )}
        {positions.map((p) => {
          const ret = p.status === 'open' ? p.return_pct : p.closed_return_pct;
          return (
            <tr
              key={p._id}
              className="border-b border-zinc-900 hover:bg-zinc-900/40 transition"
              data-testid={`sm-row-${p.symbol}`}
            >
              <td className="px-2 py-1.5 font-mono font-semibold text-zinc-100" data-testid={`sm-cell-symbol-${p.symbol}`}>
                {p.symbol}
                {p.best_tier_at_entry && (
                  <span className={`ml-1 text-[8px] px-1 py-0.5 rounded border ${TIER_TONE[p.best_tier_at_entry] || ''}`}>
                    {p.best_tier_at_entry}
                  </span>
                )}
              </td>
              <td className="px-2 py-1.5 text-zinc-400 font-mono text-[10px]">{p.entry_date}</td>
              <td className="px-2 py-1.5 text-right text-zinc-400 font-mono">{p.trading_days_elapsed ?? p.trading_days_held_at_close ?? '—'}</td>
              <td className="px-2 py-1.5 text-right text-amber-300 font-mono font-semibold">
                {fmt(p.conviction_score_at_entry, 1)}
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-zinc-300">{fmt(p.entry_price)}</td>
              <td className="px-2 py-1.5 text-right font-mono text-emerald-300/80" title="target">
                <Target size={9} className="inline mr-0.5" /> {fmt(p.target_price)}
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-rose-300/80" title="stop">
                <ShieldAlert size={9} className="inline mr-0.5" /> {fmt(p.stop_price)}
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-zinc-100">
                {fmt(p.status === 'history' ? p.closed_price : p.latest_price)}
              </td>
              <td className={`px-2 py-1.5 text-right font-mono ${toneForPct(ret)}`} data-testid={`sm-cell-return-${p.symbol}`}>
                {ret != null && (ret >= 0
                  ? <TrendingUp size={10} className="inline mr-0.5" />
                  : <TrendingDown size={10} className="inline mr-0.5" />)}
                {fmtPct(ret)}
              </td>
              <td className={`px-2 py-1.5 text-right font-mono ${toneForPct(p.return_vs_nifty500_pct)}`}>
                {fmtPct(p.return_vs_nifty500_pct)}
              </td>
              <td className={`px-2 py-1.5 text-right font-mono ${toneForPct(p.max_gain_pct)}`}>
                {fmtPct(p.max_gain_pct)}
              </td>
              <td className={`px-2 py-1.5 text-right font-mono ${toneForPct(p.max_loss_pct)}`}>
                {fmtPct(p.max_loss_pct)}
              </td>
              <td className="px-2 py-1.5 text-center">
                {p.close_reason ? (
                  <span
                    className={`text-[9px] px-1.5 py-0.5 rounded border ${CLOSE_REASON_TONE[p.close_reason] || ''}`}
                    title={`Closed on ${p.close_date} (${p.trading_days_held_at_close}d held${p.peak_gain_pct_at_close != null ? `, peak ${fmtPct(p.peak_gain_pct_at_close)}` : ''})`}
                    data-testid={`sm-close-reason-${p.symbol}`}
                  >
                    {p.close_reason === 'TARGET' && <Target size={8} className="inline mr-0.5" />}
                    {p.close_reason === 'PROFIT_LOCK' && <TrendingUp size={8} className="inline mr-0.5" />}
                    {p.close_reason === 'SMART_EXIT' && <IndianRupee size={8} className="inline mr-0.5" />}
                    {p.close_reason === 'STOP' && <ShieldAlert size={8} className="inline mr-0.5" />}
                    {p.close_reason === 'TIME' && <Clock size={8} className="inline mr-0.5" />}
                    {p.close_reason}
                  </span>
                ) : (
                  <span className="text-[10px] text-emerald-300/70 font-mono">open</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ─── Main tab ─────────────────────────────────────────────────
const SmartMoneyPortfolioTab = ({ readOnly = false, engine = 'delivery' } = {}) => {
  const cfgSel = ENGINE_CONFIG[engine] || ENGINE_CONFIG['delivery'];
  const Icon = cfgSel.icon;
  const [status, setStatus] = useState('history'); // default to history so users see the 260 trades right away
  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [watchlist, setWatchlist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sortKey, setSortKey] = useState('close_date');
  const [sortDir, setSortDir] = useState('desc');
  const [closeFilter, setCloseFilter] = useState('ALL');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [openPositions, setOpenPositions] = useState([]);
  const [historyPositions, setHistoryPositions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [posRes, sumRes, wlRes, openRes, histRes] = await Promise.all([
        axios.get(`${API}${cfgSel.routePrefix}/positions?status=${status}&limit=500`, { headers: authHeaders() }),
        axios.get(`${API}${cfgSel.routePrefix}/summary`, { headers: authHeaders() }),
        axios.get(`${API}${cfgSel.routePrefix}/watchlist?limit=100`, { headers: authHeaders() }),
        axios.get(`${API}${cfgSel.routePrefix}/positions?status=open&limit=100`, { headers: authHeaders() }),
        axios.get(`${API}${cfgSel.routePrefix}/positions?status=history&limit=20`, { headers: authHeaders() }),
      ]);
      setPositions(posRes.data?.positions || []);
      setSummary(sumRes.data || null);
      setWatchlist(wlRes.data || null);
      setOpenPositions(openRes.data?.positions || []);
      setHistoryPositions(histRes.data?.positions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to load ${cfgSel.label}`);
    } finally {
      setLoading(false);
    }
  }, [status, cfgSel.routePrefix, cfgSel.label]);

  useEffect(() => { load(); }, [load]);

  const runBackfill = async () => {
    if (!window.confirm(`Backfill will replay every cached ${engine} date and open synthetic positions above threshold. Continue?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}${cfgSel.adminPrefix}/backfill`, null, { headers: authHeaders() });
      // Handle "no upstream data" case cleanly instead of showing `undefined`
      if (r.data?.status === "no_data" || r.data?.positions_opened === undefined) {
        toast.error(
          "Backfill found no upstream signal data. `turnover_breakout_daily` " +
          "is empty — the daily-pipeline cron hasn't populated signals yet, " +
          "or historical bhavcopy hasn't been imported.",
          { duration: 10000 },
        );
      } else {
        toast.success(
          `Backfill done: ${r.data.positions_opened} opened, ` +
          `TP=${r.data.positions_auto_closed_target ?? 0} · SL=${r.data.positions_auto_closed_stop ?? 0} · ` +
          `SMART=${r.data.positions_auto_closed_smart_exit ?? 0} · TIME=${r.data.positions_auto_closed_time ?? 0}`,
          { duration: 6000 },
        );
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const runRefresh = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}${cfgSel.adminPrefix}/refresh`, null, { headers: authHeaders() });
      toast.success(
        `Refresh: ${r.data.refreshed} open · closed TP=${r.data.closed_target}, SL=${r.data.closed_stop}, TIME=${r.data.closed_time}`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const filtered = useMemo(() => {
    let list = [...positions];
    if (closeFilter !== 'ALL') list = list.filter((p) => p.close_reason === closeFilter);
    list.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return list;
  }, [positions, sortKey, sortDir, closeFilter]);

  const cfg = summary?.config || {};
  const historyStats = summary?.history || {};
  const breakdown = historyStats.breakdown_by_close_reason;

  const accentBorder = engine === 'midcap-alpha'
    ? 'border-fuchsia-500/20 bg-fuchsia-500/5'
    : 'border-amber-500/20 bg-amber-500/5';

  return (
    <div className="space-y-4" data-testid={engine === 'midcap-alpha' ? 'midcap-alpha-portfolio-tab' : 'smart-money-portfolio-tab'}>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-zinc-100 flex items-center gap-2">
            <Icon className={`w-5 h-5 ${engine === 'midcap-alpha' ? 'text-fuchsia-300' : 'text-amber-300'}`} /> {cfgSel.label}
          </h1>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            {cfgSel.tagline} · Conviction ≥ {cfg.portfolio_min_score ?? '—'} entry ·
            +{cfg.target_pct ?? 25}% target · -{cfg.stop_pct ?? 10}% stop ·
            {' '}{cfg.max_hold_trading_days ?? 21}d max hold · ₹{((cfg.notional ?? 100000) / 1000).toFixed(0)}k / position
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {!readOnly && (
            <>
              <button
                type="button"
                onClick={runRefresh}
                disabled={busy}
                className="px-3 py-1.5 text-xs rounded border border-zinc-700 hover:border-emerald-400/60 hover:text-emerald-200 transition disabled:opacity-40 font-mono flex items-center gap-1"
                data-testid="sm-refresh-btn"
              >
                <RefreshCw size={12} className={busy ? 'animate-spin' : ''} /> Refresh prices
              </button>
              <button
                type="button"
                onClick={runBackfill}
                disabled={busy}
                className={`px-3 py-1.5 text-xs rounded border font-mono transition disabled:opacity-40 ${engine === 'midcap-alpha' ? 'border-fuchsia-500/60 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-100' : 'border-amber-500/60 bg-amber-500/15 hover:bg-amber-500/25 text-amber-100'}`}
                data-testid="sm-backfill-btn"
              >
                Backfill history
              </button>
            </>
          )}
        </div>
      </div>

      {/* Today's Actions — trader-focused primary view */}
      <TodayActionsPanel
        watchlist={watchlist}
        openPositions={openPositions}
        historyPositions={historyPositions}
        engineColor={engine === 'midcap-alpha' ? 'fuchsia' : 'amber'}
        cfg={cfg}
      />

      {/* Analytics toggle */}
      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          onClick={() => setShowAnalytics((v) => !v)}
          className="px-3 py-1.5 text-xs rounded border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-mono transition"
          data-testid="toggle-analytics-btn"
        >
          {showAnalytics ? '▲ Hide' : '▼ Show'} Backtest Analytics & Full History
        </button>
        <span className="text-[10px] text-zinc-500">
          ROI · KPIs · watchlist · charts · full trade log ({summary?.history?.count || 0} trades)
        </span>
      </div>

      {/* ═══════════════ ANALYTICS SECTION (collapsible) ═══════════════ */}
      {showAnalytics && (
      <div className="space-y-4 pt-2 border-t border-zinc-800">

      {/* Hero ROI panel — portfolio-level marketing numbers */}
      <HeroROI
        portfolioRoi={summary?.portfolio_roi}
        engineColor={engine === 'midcap-alpha' ? 'fuchsia' : 'amber'}
      />

      {/* KPI strip */}
      <KpiStrip summary={summary} mode={status === 'history' ? 'history' : status === 'open' ? 'open' : 'history'} />

      {/* Breakdown by close reason (history only) */}
      {(status === 'history' || status === 'all') && breakdown && Object.keys(breakdown).length > 0 && (
        <BreakdownPanel breakdown={breakdown} />
      )}

      {/* Watchlist (always visible) */}
      <WatchlistPanel data={watchlist} loading={loading} onRefresh={load} accentClass={accentBorder} />

      {/* Returns bar chart */}
      <ReturnBarChart positions={filtered} />

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center text-xs">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Positions:</div>
        <div className="inline-flex rounded border border-zinc-800 overflow-hidden" data-testid="sm-status-toggle">
          {['open', 'history', 'all'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`px-2.5 py-1 font-mono uppercase text-[10px] ${status === s ? (engine === 'midcap-alpha' ? 'bg-fuchsia-500/25 text-fuchsia-100' : 'bg-amber-500/25 text-amber-100') : 'text-zinc-400 hover:text-zinc-200'}`}
              data-testid={`sm-status-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        {(status === 'history' || status === 'all') && (
          <div className="inline-flex rounded border border-zinc-800 overflow-hidden" data-testid="sm-close-filter">
            {['ALL', 'TARGET', 'PROFIT_LOCK', 'SMART_EXIT', 'STOP', 'TIME'].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCloseFilter(c)}
                className={`px-2.5 py-1 font-mono uppercase text-[10px] ${closeFilter === c ? 'bg-emerald-500/25 text-emerald-100' : 'text-zinc-400 hover:text-zinc-200'}`}
                data-testid={`sm-close-filter-${c.toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}
        <div className="text-[10px] text-zinc-500 ml-auto font-mono">
          {loading ? 'loading…' : `${filtered.length} position${filtered.length === 1 ? '' : 's'} shown`}
        </div>
      </div>

      {/* Table */}
      <PositionsTable
        positions={filtered}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
      />

      </div>
      )}
      {/* ═══════════════ end ANALYTICS SECTION ═══════════════ */}
    </div>
  );
};

export default SmartMoneyPortfolioTab;
